import { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Switch,
  useDisclosure,
  VStack,
  HStack,
  Badge,
  Card,
  CardBody,
  IconButton,
  Tooltip,
  ButtonGroup
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { getFuturesConfig, addFuturesConfig, updateFuturesConfig, deleteFuturesConfig } from '../api';

// 格式化数字
const formatNumber = (num) => {
  if (num === null || num === undefined) return '-';
  return Number(num).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const FuturesConfig = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentConfig, setCurrentConfig] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const data = await getFuturesConfig();
      // 将数据库中的止损比例乘以 100 用于显示
      const formattedData = data.map(config => ({
        ...config,
        stop_loss_pct: config.stop_loss_pct * 100
      }));
      setConfigs(formattedData);
    } catch (error) {
      toast({
        title: '获取配置失败',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const handleAdd = () => {
    setCurrentConfig({
      symbol: '',
      name: '',
      stop_loss_pct: 0,
      enabled: true
    });
    onOpen();
  };

  const handleEdit = (config) => {
    setCurrentConfig({
      ...config
    });
    onOpen();
  };

  const handleDelete = async (id) => {
    if (window.confirm('确定要删除这个配置吗？')) {
      try {
        await deleteFuturesConfig(id);
        toast({
          title: '删除成功',
          status: 'success',
          duration: 2000,
        });
        fetchConfigs();
      } catch (error) {
        toast({
          title: '删除失败',
          description: error.message,
          status: 'error',
          duration: 3000,
        });
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const configToSave = {
        ...currentConfig,
        stop_loss_pct: parseFloat(currentConfig.stop_loss_pct) / 100
      };
      
      if (currentConfig.id) {
        await updateFuturesConfig(currentConfig.id, configToSave);
      } else {
        await addFuturesConfig(configToSave);
      }
      toast({
        title: `${currentConfig.id ? '更新' : '添加'}成功`,
        status: 'success',
        duration: 2000,
      });
      onClose();
      fetchConfigs();
    } catch (error) {
      toast({
        title: `${currentConfig.id ? '更新' : '添加'}失败`,
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Card>
      <CardBody>
        <HStack mb={4} spacing={4} justify="space-between">
          <Text fontSize="xl" fontWeight="bold">期货配置</Text>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            size="sm"
            onClick={handleAdd}
          >
            添加配置
          </Button>
        </HStack>

        {loading ? (
          <Spinner />
        ) : configs.length === 0 ? (
          <Text>暂无配置</Text>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>标的</Th>
                  <Th>名称</Th>
                  <Th isNumeric>止损百分比</Th>
                  <Th>状态</Th>
                  <Th>创建时间</Th>
                  <Th>更新时间</Th>
                  <Th>操作</Th>
                </Tr>
              </Thead>
              <Tbody>
                {configs.map((config) => (
                  <Tr key={config.id}>
                    <Td>
                      <Badge colorScheme="purple" fontSize="sm">
                        {config.symbol}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontWeight="medium">{config.name}</Text>
                    </Td>
                    <Td isNumeric>
                      <Badge colorScheme="orange" variant="outline" fontSize="sm">
                        {formatNumber(config.stop_loss_pct)}%
                      </Badge>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={config.enabled ? 'green' : 'gray'}
                        variant="subtle"
                        fontSize="sm"
                      >
                        {config.enabled ? '启用' : '禁用'}
                      </Badge>
                    </Td>
                    <Td fontSize="sm">{new Date(config.created_at).toLocaleString()}</Td>
                    <Td fontSize="sm">{new Date(config.updated_at).toLocaleString()}</Td>
                    <Td>
                      <ButtonGroup size="sm" spacing={2}>
                        <Tooltip label="编辑" placement="top">
                          <IconButton
                            icon={<EditIcon />}
                            onClick={() => handleEdit(config)}
                            colorScheme="blue"
                            variant="ghost"
                            aria-label="编辑配置"
                          />
                        </Tooltip>
                        <Tooltip label="删除" placement="top">
                          <IconButton
                            icon={<DeleteIcon />}
                            onClick={() => handleDelete(config.id)}
                            colorScheme="red"
                            variant="ghost"
                            aria-label="删除配置"
                          />
                        </Tooltip>
                      </ButtonGroup>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        <Modal isOpen={isOpen} onClose={onClose} size="md">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {currentConfig?.id ? '编辑配置' : '添加配置'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>标的</FormLabel>
                  <Input
                    value={currentConfig?.symbol || ''}
                    onChange={(e) => setCurrentConfig({
                      ...currentConfig,
                      symbol: e.target.value
                    })}
                    placeholder="请输入标的代码"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>名称</FormLabel>
                  <Input
                    value={currentConfig?.name || ''}
                    onChange={(e) => setCurrentConfig({
                      ...currentConfig,
                      name: e.target.value
                    })}
                    placeholder="请输入名称"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>止损百分比</FormLabel>
                  <Input
                    type="number"
                    step="0.1"
                    value={currentConfig?.stop_loss_pct || ''}
                    onChange={(e) => setCurrentConfig({
                      ...currentConfig,
                      stop_loss_pct: parseFloat(e.target.value)
                    })}
                    placeholder="请输入止损百分比"
                  />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">启用</FormLabel>
                  <Switch
                    isChecked={currentConfig?.enabled}
                    onChange={(e) => setCurrentConfig({
                      ...currentConfig,
                      enabled: e.target.checked
                    })}
                    colorScheme="green"
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                取消
              </Button>
              <Button colorScheme="blue" onClick={handleSubmit}>
                保存
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </CardBody>
    </Card>
  );
};

export default FuturesConfig; 